import React, { useState, useMemo, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useFinancial, getSortedAccounts, normalizeToMonthly } from '../context/FinancialContext';
import WealthProjectionChart from '../components/charts/WealthProjectionChart';
import MetricCard from '../components/cards/MetricCard';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BulkEntryModal from '../components/modals/BulkEntryModal';
import SystemGuideModal from '../components/modals/SystemGuideModal';
import RoadmapDiagramModal from '../components/modals/RoadmapDiagramModal';
import RoadmapWalkthroughModal from '../components/modals/roadmap/RoadmapWalkthroughModal';
import WelcomeTourModal from '../components/modals/WelcomeTourModal';
import { LIFE_SCENARIOS } from '../data/lifeScenarios';

const {
  FiPlus,
  FiTrash2,
  FiEdit2,
  FiArrowDown,
  FiShield,
  FiBriefcase,
  FiChevronDown,
  FiLayers,
  FiZap,
  FiCompass,
  FiFilter,
  FiRepeat,
  FiSettings,
  FiCheck,
  FiTrendingDown,
  FiClock,
  FiChevronLeft,
  FiChevronRight,
  FiHelpCircle
} = FiIcons;

const GlobalFlowFilter = ({ selected, onChange }) => {
  const { state } = useFinancial();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const allAccounts = useMemo(() => getSortedAccounts(state.customAccounts), [state.customAccounts]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleAccount = (id) => {
    if (selected.includes(id)) {
      onChange(selected.filter((x) => x !== id));
    } else {
      onChange([...selected, id]);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Filter Accounts"
        className={`p-2 rounded-lg transition-all ${selected.length > 0 ? 'bg-blue-50 text-blue-600' : 'text-slate-400 hover:bg-slate-50'}`}
      >
        <SafeIcon icon={FiFilter} />
      </button>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.95 }}
            className="absolute right-0 mt-2 w-48 md:w-56 bg-white rounded-xl shadow-2xl border border-slate-100 z-[100] overflow-hidden p-2 transform origin-top-right"
          >
            <div className="flex justify-between items-center px-2 pb-2 mb-2 border-b border-slate-50">
              <span className="text-[10px] font-black uppercase text-slate-400 tracking-widest">Filter Accounts</span>
              {selected.length > 0 && (
                <button onClick={() => onChange([])} className="text-[9px] font-bold text-blue-600 uppercase">
                  Clear
                </button>
              )}
            </div>
            <div className="max-h-60 overflow-y-auto custom-scrollbar space-y-1">
              {allAccounts.map((acc) => (
                <button
                  key={acc.id}
                  onClick={() => toggleAccount(acc.id)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors text-left"
                >
                  <div
                    className={`w-4 h-4 rounded flex items-center justify-center border transition-colors ${
                      selected.includes(acc.id) ? 'bg-blue-600 border-blue-600 text-white' : 'border-slate-300'
                    }`}
                  >
                    {selected.includes(acc.id) && <SafeIcon icon={FiCheck} className="text-[10px]" />}
                  </div>
                  <span className="text-[11px] font-bold text-slate-700 truncate">{acc.name}</span>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Dashboard = () => {
  const { state, dispatch } = useFinancial();
  const [showAddModal, setShowAddModal] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [showSystemGuide, setShowSystemGuide] = useState(false);
  const [showDiagram, setShowDiagram] = useState(false);
  const [showWalkthrough, setShowWalkthrough] = useState(false);
  const [showWelcomeTour, setShowWelcomeTour] = useState(false);

  // Auto-trigger welcome tour on initial visit
  useEffect(() => {
    const hasSeenTour = localStorage.getItem('wealthos_has_seen_tour');
    if (!hasSeenTour) {
      setShowWelcomeTour(true);
      localStorage.setItem('wealthos_has_seen_tour', 'true');
    }
  }, []);

  const displayFrequency = state.settings?.displayFrequency || 'default';
  const flowFilters = state.settings?.flowFilters || [];
  const setDisplayFrequency = (freq) => dispatch({ type: 'UPDATE_SETTINGS', payload: { displayFrequency: freq } });
  const setFlowFilters = (filters) => dispatch({ type: 'UPDATE_SETTINGS', payload: { flowFilters: filters } });

  const [showWealthFilter, setShowWealthFilter] = useState(false);
  const [showModelFilter, setShowModelFilter] = useState(false);
  const [selectedWealthItems, setSelectedWealthItems] = useState(['total']);
  const [mobileMetricsExpanded, setMobileMetricsExpanded] = useState(false);

  // Scenarios horizontal scroll controls
  const scenariosScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scenariosScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scenariosScrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener('resize', checkScroll);
    return () => window.removeEventListener('resize', checkScroll);
  }, []);

  const scrollScenarios = (direction) => {
    if (scenariosScrollRef.current) {
      scenariosScrollRef.current.scrollBy({ left: direction === 'left' ? -250 : 250, behavior: 'smooth' });
      setTimeout(checkScroll, 300);
    }
  };

  const wealthDropdownRef = useRef(null);
  const modelDropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wealthDropdownRef.current && !wealthDropdownRef.current.contains(event.target)) setShowWealthFilter(false);
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target)) setShowModelFilter(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const formatCurrency = (amt) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amt || 0);

  const convertAmount = (amount, itemFreq, targetFreq) => {
    let monthly = amount;
    if (itemFreq === 'weekly') monthly = (amount * 52) / 12;
    else if (itemFreq === 'biweekly') monthly = (amount * 26) / 12;
    else if (itemFreq === 'yearly') monthly = amount / 12;

    if (targetFreq === 'weekly') return (monthly * 12) / 52;
    if (targetFreq === 'biweekly') return (monthly * 12) / 26;
    if (targetFreq === 'yearly') return monthly * 12;
    return monthly;
  };

  const allAccountsSorted = useMemo(() => getSortedAccounts(state.customAccounts), [state.customAccounts]);
  const calculationFreq = displayFrequency === 'default' ? 'monthly' : displayFrequency;

  // Monthly totals calculation
  const monthlyTotals = useMemo(() => {
    const sum = (list) =>
      list.reduce((acc, item) => acc + normalizeToMonthly(item.amount, item.frequency || 'monthly'), 0);
    const income = sum(state.incomeStreams);
    const expenses = sum(state.expenses);
    const investments = sum(state.investmentBuckets);
    return { income, expenses, investments, surplus: income - expenses - investments };
  }, [state.incomeStreams, state.expenses, state.investmentBuckets]);

  const accountTotals = useMemo(() => {
    const totals = {};
    allAccountsSorted.forEach((acc) => (totals[acc.id] = 0));
    const processList = (list) => {
      list.forEach((item) => {
        const val = convertAmount(item.amount, item.frequency || 'monthly', calculationFreq);
        if (totals[item.accountType] !== undefined) totals[item.accountType] += val;
      });
    };
    processList(state.incomeStreams);
    processList(state.expenses);
    processList(state.investmentBuckets);
    return totals;
  }, [state, calculationFreq, allAccountsSorted]);

  const handleBulkSave = (entries) => {
    const type =
      showAddModal === 'income' ? 'ADD_INCOME' : showAddModal === 'expense' ? 'ADD_EXPENSE' : 'ADD_INVESTMENT';
    entries.forEach((entry, index) => {
      dispatch({
        type,
        payload: {
          id: Date.now() + index + Math.random(),
          name: entry.name,
          amount: parseFloat(entry.amount),
          frequency: entry.frequency,
          accountType: entry.accountType,
          icon: 'Target',
          color: 'blue'
        }
      });
    });
    setShowAddModal(null);
  };

  const handleEditSave = (entries) => {
    if (!editTarget || entries.length === 0) {
      setEditTarget(null);
      return;
    }
    const entry = entries[0];
    const type =
      editTarget.category === 'income'
        ? 'UPDATE_INCOME'
        : editTarget.category === 'expense'
        ? 'UPDATE_EXPENSE'
        : 'UPDATE_INVESTMENT';
    dispatch({
      type,
      payload: {
        id: editTarget.item.id,
        name: entry.name,
        amount: parseFloat(entry.amount) || 0,
        frequency: entry.frequency,
        accountType: entry.accountType
      }
    });
    setEditTarget(null);
  };

  const filterList = (list) => {
    if (flowFilters.length === 0) return list;
    return list.filter((item) => flowFilters.includes(item.accountType));
  };

  const toggleWealthItem = (id) => {
    if (id === 'total') {
      setSelectedWealthItems(['total']);
    } else {
      const newItems = selectedWealthItems.filter((i) => i !== 'total');
      if (newItems.includes(id)) {
        setSelectedWealthItems(newItems.filter((i) => i !== id));
      } else {
        setSelectedWealthItems([...newItems, id]);
      }
    }
    setShowWealthFilter(false);
  };

  const FlowItem = ({ item, category }) => {
    const icon = category === 'income' ? FiBriefcase : category === 'expense' ? FiArrowDown : FiLayers;
    const accentColor = category === 'income' ? 'emerald' : category === 'expense' ? 'rose' : 'blue';
    const displayVal =
      displayFrequency === 'default'
        ? item.amount
        : convertAmount(item.amount, item.frequency || 'monthly', displayFrequency);
    const displayFreqTag = displayFrequency === 'default' ? item.frequency || 'monthly' : displayFrequency;

    return (
      <div className="flex justify-between items-center p-3 bg-slate-50/80 rounded-2xl border border-slate-100/50 group">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`w-10 h-10 bg-white rounded-xl shadow-sm flex items-center justify-center text-${accentColor}-600 flex-shrink-0`}
          >
            <SafeIcon icon={icon} className="text-base" />
          </div>
          <div className="min-w-0 text-left">
            <p className="text-xs font-black text-slate-900 truncate max-w-[140px] leading-tight">{item.name}</p>
            <div className="flex items-center gap-1 mt-0.5">
              <SafeIcon icon={FiClock} className="text-[8px] text-slate-400" />
              <p className="text-[8px] font-bold uppercase text-slate-400 tracking-widest">{displayFreqTag}</p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          <span className="text-sm font-black text-slate-900 block leading-none mr-1">
            {formatCurrency(displayVal)}
          </span>
          <button
            onClick={() => setEditTarget({ category, item })}
            className="p-1.5 text-slate-300 hover:text-blue-500 transition-colors"
            title="Edit"
          >
            <SafeIcon icon={FiEdit2} className="text-sm" />
          </button>
          <button
            onClick={() =>
              dispatch({
                type:
                  category === 'income'
                    ? 'REMOVE_INCOME'
                    : category === 'expense'
                    ? 'REMOVE_EXPENSE'
                    : 'REMOVE_INVESTMENT',
                id: item.id
              })
            }
            className="p-1.5 text-slate-300 hover:text-rose-500 transition-colors"
            title="Delete"
          >
            <SafeIcon icon={FiTrash2} className="text-sm" />
          </button>
        </div>
      </div>
    );
  };

  const severityPercent = (state.goals.downturnSeverity / 80) * 100;
  const inflationPercent = (state.goals.inflationRate / 15) * 100;

  const filteredIncome = filterList(state.incomeStreams);
  const filteredExpenses = filterList(state.expenses);
  const filteredInvestments = filterList(state.investmentBuckets);

  const totalIncome = filteredIncome.reduce(
    (acc, item) => acc + convertAmount(item.amount, item.frequency || 'monthly', calculationFreq),
    0
  );
  const totalExpenses = filteredExpenses.reduce(
    (acc, item) => acc + convertAmount(item.amount, item.frequency || 'monthly', calculationFreq),
    0
  );
  const totalInvestments = filteredInvestments.reduce(
    (acc, item) => acc + convertAmount(item.amount, item.frequency || 'monthly', calculationFreq),
    0
  );

  const surplus = monthlyTotals.surplus;
  const hasSurplus = Math.round(surplus) > 0;
  const isOverAllocated = Math.round(surplus) < 0;

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header onOpenWalkthrough={() => setShowWalkthrough(true)} />

      <main className="max-w-[1600px] mx-auto p-4 md:p-6 space-y-6">
        {/* Mobile Metrics Toggle */}
        <div className="md:hidden">
          <button
            onClick={() => setMobileMetricsExpanded(!mobileMetricsExpanded)}
            className="w-full bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-between shadow-sm"
          >
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center border border-blue-100/50 text-blue-600">
                <SafeIcon icon={FiShield} className="text-lg" />
              </div>
              <div className="text-left">
                <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-0.5">Growth Summary</p>
                <p className="text-sm font-black text-slate-900">
                  {formatCurrency(state.calculations.projectedWealth)} Projected
                </p>
              </div>
            </div>
            <SafeIcon
              icon={FiChevronDown}
              className={`text-slate-400 transition-transform ${mobileMetricsExpanded ? 'rotate-180' : ''}`}
            />
          </button>
        </div>

        {/* Global Metrics Row */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative ${mobileMetricsExpanded ? 'grid' : 'hidden md:grid'}`}>
          <MetricCard
            title="Current Capital"
            value={formatCurrency(state.goals.startingNetWorth)}
            icon={FiShield}
            color="blue"
            subtitle="Nominal Base"
            explanation="The total amount of liquid and invested assets you are starting with."
          />
          <MetricCard
            title="Investment Total"
            value={formatCurrency(monthlyTotals.investments)}
            icon={FiLayers}
            color="green"
            subtitle="Monthly Fuel"
            explanation="The total amount you are saving and investing every month."
          />
          <MetricCard
            title="Net Surplus"
            value={formatCurrency(surplus)}
            icon={FiZap}
            color="purple"
            subtitle="Monthly Free Cash"
            explanation="Monthly money left over after all income, expenses, and planned investments. Always shown per month."
          />
          <MetricCard
            title="Time Horizon"
            value={`${state.calculations.yearsToGoal} YRS`}
            icon={FiCompass}
            color="orange"
            subtitle="Runway"
            explanation="Years between current age and target retirement age."
          />
        </div>

        <div className="flex flex-col lg:flex-row gap-6 items-start mt-6 md:mt-0">
          {/* Left Column: Wealth Trajectory Chart */}
          <div className="w-full lg:w-8/12 flex flex-col gap-6">
            <div className="bg-white rounded-[2rem] p-5 md:p-10 border border-slate-100 shadow-sm flex flex-col relative overflow-hidden">
              <div className="flex flex-col md:flex-row justify-between items-start mb-6 md:mb-8 z-20 gap-6">
                <div className="text-left w-full md:w-auto">
                  <div className="flex items-center gap-3">
                    <h2 className="text-xl md:text-2xl font-black text-slate-900 tracking-tighter whitespace-nowrap">
                      Wealth Trajectory
                    </h2>
                    <button
                      onClick={() => setShowWelcomeTour(true)}
                      className="p-1.5 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                      title="Platform Tour"
                    >
                      <SafeIcon icon={FiHelpCircle} className="text-sm" />
                    </button>
                  </div>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">
                    Nominal vs. Purchasing Power
                  </p>

                  <div className="flex flex-wrap gap-2 mt-4 relative">
                    {/* Series Filter Dropdown */}
                    <div className="relative" ref={wealthDropdownRef}>
                      <button
                        onClick={() => setShowWealthFilter(!showWealthFilter)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg text-[9px] font-black uppercase text-slate-600 tracking-widest hover:bg-slate-100 transition-colors"
                      >
                        <SafeIcon icon={FiLayers} className="text-[10px]" /> Wealth Series
                        <SafeIcon icon={FiChevronDown} className="text-[10px]" />
                      </button>
                      <AnimatePresence>
                        {showWealthFilter && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            className="absolute left-0 mt-2 w-56 bg-white rounded-xl shadow-2xl border border-slate-100 z-[100] overflow-hidden p-1"
                          >
                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest px-3 py-2">
                              Select Series
                            </p>
                            <button
                              onClick={() => toggleWealthItem('total')}
                              className={`w-full text-left px-3 py-2.5 rounded-lg text-[10px] font-black tracking-widest transition-colors flex justify-between items-center ${
                                selectedWealthItems.includes('total') ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-600'
                              }`}
                            >
                              <span>Total Accumulated</span>
                              {selectedWealthItems.includes('total') && <SafeIcon icon={FiCheck} />}
                            </button>
                            {state.investmentBuckets.map((b) => (
                              <button
                                key={b.id}
                                onClick={() => toggleWealthItem(b.id.toString())}
                                className={`w-full text-left px-3 py-2.5 rounded-lg text-[10px] font-black tracking-widest transition-colors flex justify-between items-center mt-0.5 ${
                                  selectedWealthItems.includes(b.id.toString())
                                    ? 'bg-blue-600 text-white'
                                    : 'hover:bg-slate-50 text-slate-600'
                                }`}
                              >
                                <span>{b.name}</span>
                                {selectedWealthItems.includes(b.id.toString()) && <SafeIcon icon={FiCheck} />}
                              </button>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Model Selector Dropdown */}
                    <div className="relative" ref={modelDropdownRef}>
                      <button
                        onClick={() => setShowModelFilter(!showModelFilter)}
                        className="flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg text-[9px] font-black uppercase text-slate-600 tracking-widest hover:bg-slate-100 transition-colors"
                      >
                        <SafeIcon icon={FiZap} className="text-[10px]" /> Model:{' '}
                        {state.investmentSchedule?.type === 'linear_step' ? 'Linear Step' : 'Fixed Plan'}
                        <SafeIcon icon={FiChevronDown} className="text-[10px]" />
                      </button>
                      <AnimatePresence>
                        {showModelFilter && (
                          <motion.div
                            initial={{ opacity: 0, y: 10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 10, scale: 0.95 }}
                            className="absolute right-0 md:left-0 md:right-auto mt-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-100 z-[100] overflow-hidden p-1"
                          >
                            <p className="text-[8px] font-bold text-slate-400 uppercase tracking-widest px-3 py-2">
                              Growth Modeling
                            </p>
                            <button
                              onClick={() => {
                                dispatch({ type: 'UPDATE_INVESTMENT_SCHEDULE', payload: { type: 'fixed' } });
                                setShowModelFilter(false);
                              }}
                              className={`w-full text-left px-3 py-2.5 rounded-lg text-[10px] font-black tracking-widest transition-colors flex justify-between items-center ${
                                state.investmentSchedule?.type === 'fixed' ? 'bg-blue-600 text-white' : 'hover:bg-slate-50 text-slate-600'
                              }`}
                            >
                              <span>Fixed Plan</span>
                              {state.investmentSchedule?.type === 'fixed' && <SafeIcon icon={FiCheck} />}
                            </button>
                            <button
                              onClick={() => {
                                dispatch({ type: 'UPDATE_INVESTMENT_SCHEDULE', payload: { type: 'linear_step' } });
                              }}
                              className={`w-full text-left px-3 py-2.5 rounded-lg text-[10px] font-black tracking-widest transition-colors flex justify-between items-center mt-0.5 ${
                                state.investmentSchedule?.type === 'linear_step'
                                  ? 'bg-blue-600 text-white'
                                  : 'hover:bg-slate-50 text-slate-600'
                              }`}
                            >
                              <span>Linear Step</span>
                              {state.investmentSchedule?.type === 'linear_step' && <SafeIcon icon={FiCheck} />}
                            </button>

                            {state.investmentSchedule?.type === 'linear_step' && (
                              <div className="mt-1 p-3 bg-slate-50 rounded-lg border border-slate-100">
                                <p className="text-[8px] font-black text-slate-400 uppercase tracking-widest mb-2">
                                  Annual Increase
                                </p>
                                <div className="flex gap-2">
                                  <div className="relative flex-1">
                                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400 font-black text-xs">
                                      $
                                    </span>
                                    <input
                                      type="number"
                                      value={state.investmentSchedule?.stepAmount || ''}
                                      onChange={(e) =>
                                        dispatch({
                                          type: 'UPDATE_INVESTMENT_SCHEDULE',
                                          payload: { stepAmount: parseFloat(e.target.value) || 0 }
                                        })
                                      }
                                      className="w-full pl-5 pr-2 py-1.5 bg-white border border-slate-200 rounded-md text-[10px] font-black text-slate-900 focus:ring-1 focus:ring-blue-500 outline-none transition-all"
                                      placeholder="500"
                                    />
                                  </div>
                                  <div className="relative flex-1">
                                    <select
                                      className="w-full appearance-none px-2 py-1.5 bg-white border border-slate-200 rounded-md text-[10px] font-black text-slate-900 outline-none focus:ring-1 focus:ring-blue-500 cursor-pointer pr-6 shadow-sm"
                                      value={state.investmentSchedule?.stepFrequency || 'monthly'}
                                      onChange={(e) =>
                                        dispatch({
                                          type: 'UPDATE_INVESTMENT_SCHEDULE',
                                          payload: { stepFrequency: e.target.value }
                                        })
                                      }
                                    >
                                      <option value="weekly">Weekly</option>
                                      <option value="biweekly">Bi-Weekly</option>
                                      <option value="monthly">Monthly</option>
                                      <option value="yearly">Yearly</option>
                                    </select>
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                                      <SafeIcon icon={FiChevronDown} className="text-[8px]" />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            )}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  </div>

                  <div className="md:hidden mt-6 text-left">
                    <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-0.5">
                      Est. Net Worth
                    </p>
                    <p className="text-3xl font-black text-blue-600 tracking-tighter leading-none">
                      {formatCurrency(state.calculations.projectedWealth)}
                    </p>
                    <p className="text-[10px] font-black uppercase text-rose-500 tracking-widest mt-1.5">
                      Real: {formatCurrency(state.calculations.realProjectedWealth)}
                    </p>
                  </div>
                </div>

                <div className="hidden md:block text-right">
                  <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-0.5">Est. Net Worth</p>
                  <p className="text-3xl md:text-4xl font-black text-blue-600 tracking-tighter leading-none">
                    {formatCurrency(state.calculations.projectedWealth)}
                  </p>
                  <p className="text-[10px] font-black uppercase text-rose-500 tracking-widest mt-1.5">
                    Real: {formatCurrency(state.calculations.realProjectedWealth)}
                  </p>
                </div>
              </div>

              <div className="h-[300px] md:h-[400px] w-full overflow-hidden">
                <WealthProjectionChart selectedItems={selectedWealthItems} />
              </div>

              {/* Stress Test Controls */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6 pt-6 border-t border-slate-50 z-20 relative">
                <div className="space-y-3">
                  <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest flex items-center gap-1">
                    <SafeIcon icon={FiTrendingDown} className="text-[10px]" /> Crash Year
                  </label>
                  <input
                    type="number"
                    placeholder="e.g. 5"
                    className="w-full px-4 py-2.5 bg-slate-50 rounded-xl border-none outline-none focus:ring-2 focus:ring-blue-500 text-xs font-bold text-slate-900 transition-all"
                    value={state.goals.downturnYear || ''}
                    onChange={(e) =>
                      dispatch({ type: 'UPDATE_GOALS', payload: { downturnYear: parseInt(e.target.value) || 0 } })
                    }
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest flex items-center gap-1">
                      Crash Severity
                    </label>
                    <span className="text-[10px] font-black text-slate-900">-{state.goals.downturnSeverity}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="80"
                    style={{
                      background: `linear-gradient(to right, #0f172a ${severityPercent}%, #f1f5f9 ${severityPercent}%)`
                    }}
                    className="w-full h-1.5 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-slate-900 [&::-webkit-slider-thumb]:rounded-full shadow-inner"
                    value={state.goals.downturnSeverity}
                    onChange={(e) =>
                      dispatch({ type: 'UPDATE_GOALS', payload: { downturnSeverity: parseInt(e.target.value) || 0 } })
                    }
                  />
                </div>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <label className="text-[9px] font-black uppercase text-slate-400 tracking-widest flex items-center gap-1">
                      Inflation Target
                    </label>
                    <span className="text-[10px] font-black text-slate-900">{state.goals.inflationRate}%</span>
                  </div>
                  <input
                    type="range"
                    min="0"
                    max="15"
                    step="0.5"
                    style={{
                      background: `linear-gradient(to right, #f43f5e ${inflationPercent}%, #f1f5f9 ${inflationPercent}%)`
                    }}
                    className="w-full h-1.5 rounded-lg appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:bg-rose-500 [&::-webkit-slider-thumb]:rounded-full shadow-inner"
                    value={state.goals.inflationRate}
                    onChange={(e) =>
                      dispatch({ type: 'UPDATE_GOALS', payload: { inflationRate: parseFloat(e.target.value) || 0 } })
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Budget Engine Input & Routing */}
          <div className="w-full lg:w-4/12 flex flex-col gap-6">
            <div className="bg-white rounded-[2rem] border border-slate-100 shadow-sm flex flex-col lg:min-h-[850px] overflow-hidden">
              <div className="p-6 pb-2">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-xl font-black text-slate-900 tracking-tight">Budget</h2>
                  <div className="flex gap-1.5 items-center">
                    <GlobalFlowFilter selected={flowFilters} onChange={setFlowFilters} />
                    <div className="w-px h-6 bg-slate-100 mx-1"></div>
                    <button
                      onClick={() => setShowAddModal('income')}
                      className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100 transition-colors"
                      title="Add Income"
                    >
                      <SafeIcon icon={FiPlus} />
                    </button>
                    <button
                      onClick={() => setShowAddModal('expense')}
                      className="p-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition-colors"
                      title="Add Expense"
                    >
                      <SafeIcon icon={FiPlus} />
                    </button>
                    <button
                      onClick={() => setShowAddModal('investment')}
                      className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
                      title="Add Investment"
                    >
                      <SafeIcon icon={FiPlus} />
                    </button>
                  </div>
                </div>

                {/* Life Scenarios Horizontal Scroll */}
                <div className="mb-4 space-y-2">
                  <p className="text-[8px] font-black uppercase text-slate-400 tracking-widest px-1">
                    Load Life Scenario
                  </p>
                  <div className="relative group">
                    {canScrollLeft && (
                      <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-white to-transparent z-10 flex items-center justify-start pl-1">
                        <button
                          onClick={() => scrollScenarios('left')}
                          className="bg-white border border-slate-100 text-slate-600 rounded-full p-1 shadow-sm hover:bg-slate-50 transition-all"
                        >
                          <SafeIcon icon={FiChevronLeft} className="text-sm" />
                        </button>
                      </div>
                    )}
                    <div
                      ref={scenariosScrollRef}
                      onScroll={checkScroll}
                      className="flex gap-2 overflow-x-auto scroll-smooth [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] pb-2"
                    >
                      {LIFE_SCENARIOS.map((scenario) => (
                        <button
                          key={scenario.id}
                          onClick={() => dispatch({ type: 'LOAD_SCENARIO', payload: scenario.payload })}
                          className={`flex-shrink-0 flex items-center gap-2 p-2 pr-3 rounded-xl border border-slate-100 bg-slate-50 hover:bg-white hover:shadow-sm ${scenario.style.hover} transition-all`}
                        >
                          <div className={`w-6 h-6 rounded-lg ${scenario.style.icon} flex items-center justify-center`}>
                            <SafeIcon icon={scenario.icon} className="text-[10px]" />
                          </div>
                          <div className="text-left">
                            <p className="text-[10px] font-black text-slate-900 leading-tight">{scenario.name}</p>
                            <p className="text-[8px] font-bold text-slate-400">{scenario.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                    {canScrollRight && (
                      <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-white to-transparent z-10 flex items-center justify-end pr-1">
                        <button
                          onClick={() => scrollScenarios('right')}
                          className="bg-white border border-slate-100 text-slate-600 rounded-full p-1 shadow-sm hover:bg-slate-50 transition-all"
                        >
                          <SafeIcon icon={FiChevronRight} className="text-sm" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Display Frequency Selector */}
                <div className="flex bg-slate-50 p-1 rounded-xl border border-slate-100 mb-4">
                  {['default', 'weekly', 'biweekly', 'monthly', 'yearly'].map((freq) => (
                    <button
                      key={freq}
                      onClick={() => setDisplayFrequency(freq)}
                      className={`flex-1 py-2 text-[8px] font-black uppercase tracking-widest rounded-lg transition-all ${
                        displayFrequency === freq
                          ? 'bg-white text-blue-600 shadow-sm border border-slate-100'
                          : 'text-slate-400 hover:text-slate-600'
                      }`}
                    >
                      {freq === 'biweekly' ? 'Bi-Weekly' : freq}
                    </button>
                  ))}
                </div>
              </div>

              {/* Flows Listing */}
              <div className="flex-1 overflow-y-auto px-6 space-y-6 custom-scrollbar pb-6 overflow-x-visible">
                {/* Income */}
                <section className="relative z-[40]">
                  <div className="flex justify-between items-center mb-2 px-1">
                    <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">INCOME</h3>
                    <span className="text-[10px] font-black text-emerald-600">+{formatCurrency(totalIncome)}</span>
                  </div>
                  <div className="space-y-2">
                    {filteredIncome.map((s) => (
                      <FlowItem key={s.id} item={s} category="income" />
                    ))}
                  </div>
                </section>

                {/* Expenses */}
                <section className="relative z-[30]">
                  <div className="flex justify-between items-center mb-2 px-1">
                    <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">EXPENSES</h3>
                    <span className="text-[10px] font-black text-rose-600">-{formatCurrency(totalExpenses)}</span>
                  </div>
                  <div className="space-y-2">
                    {filteredExpenses.map((e) => (
                      <FlowItem key={e.id} item={e} category="expense" />
                    ))}
                  </div>
                </section>

                {/* Investments */}
                <section className="relative z-[20]">
                  <div className="flex justify-between items-center mb-2 px-1">
                    <h3 className="text-[10px] font-black uppercase text-slate-400 tracking-widest">INVESTMENTS</h3>
                    <span className="text-[10px] font-black text-blue-600">{formatCurrency(totalInvestments)}</span>
                  </div>
                  {(hasSurplus || isOverAllocated) && (
                    <button
                      onClick={() => hasSurplus && setShowAddModal('investment')}
                      className={`w-full mb-2 p-3 rounded-2xl border flex justify-between items-center text-left transition-all ${
                        hasSurplus
                          ? 'bg-blue-50/60 border-blue-100 hover:bg-blue-50 cursor-pointer'
                          : 'bg-rose-50/60 border-rose-100 cursor-default'
                      }`}
                    >
                      <div>
                        <p
                          className={`text-[8px] font-black uppercase tracking-widest ${
                            hasSurplus ? 'text-blue-500' : 'text-rose-500'
                          }`}
                        >
                          {hasSurplus ? 'Available to Invest' : 'Over-Allocated'}
                        </p>
                        <p className="text-[8px] font-bold text-slate-400 mt-0.5">
                          {hasSurplus ? 'Monthly income left after expenses & investments' : 'Monthly outflow exceeds income'}
                        </p>
                      </div>
                      <span className={`text-sm font-black ${hasSurplus ? 'text-blue-600' : 'text-rose-600'}`}>
                        {formatCurrency(Math.abs(surplus))}
                        <span className="text-[8px] ml-0.5">/mo</span>
                      </span>
                    </button>
                  )}
                  <div className="space-y-2">
                    {filteredInvestments.map((inv) => (
                      <FlowItem key={inv.id} item={inv} category="investment" />
                    ))}
                  </div>
                </section>
              </div>

              {/* Aggregated Accounts Routing Footer */}
              <div className="bg-slate-50 border-t border-slate-100 p-6 space-y-3">
                <div className="flex items-center gap-2 mb-2">
                  <SafeIcon icon={FiRepeat} className="text-slate-400 text-xs" />
                  <h4 className="text-[10px] font-black uppercase text-slate-500 tracking-widest">
                    Account Allocation ({calculationFreq})
                  </h4>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setShowSystemGuide(true)}
                    className="bg-indigo-50 p-3 rounded-xl border border-indigo-100 shadow-sm group hover:shadow-md hover:bg-white transition-all text-left relative overflow-hidden"
                  >
                    <div className="flex justify-between items-start mb-1 relative z-10">
                      <p className="text-[8px] font-black text-indigo-400 uppercase truncate pr-2">Budget Alerts</p>
                      <SafeIcon icon={FiSettings} className="text-indigo-500 text-[10px]" />
                    </div>
                    <p className="text-[10px] font-black text-indigo-900 relative z-10 leading-tight uppercase tracking-tighter">
                      Setup Guide
                    </p>
                  </button>
                  {allAccountsSorted.map((acc) => (
                    <div
                      key={acc.id}
                      className="bg-white p-3 rounded-xl border border-slate-100 shadow-sm group hover:border-blue-200 transition-all text-left relative overflow-hidden"
                    >
                      <div className="flex justify-between items-start mb-1 relative z-10">
                        <p className="text-[8px] font-black text-slate-400 uppercase truncate pr-2">{acc.name}</p>
                        <div
                          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                            acc.category === 'income'
                              ? 'bg-emerald-400'
                              : acc.category === 'drain'
                              ? 'bg-rose-400'
                              : 'bg-blue-400'
                          } opacity-80`}
                        />
                      </div>
                      <div className="flex justify-between items-end relative z-10">
                        <p className="text-xs font-black text-slate-900">{formatCurrency(accountTotals[acc.id] || 0)}</p>
                        {acc.tag && (
                          <span className="text-[7px] font-black text-slate-400 uppercase tracking-tighter bg-slate-50 px-1 py-0.5 rounded border border-slate-100/50">
                            {acc.tag}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Modals */}
      <AnimatePresence>
        {showAddModal && (
          <BulkEntryModal
            isOpen={!!showAddModal}
            type={showAddModal}
            onClose={() => setShowAddModal(null)}
            onSave={handleBulkSave}
          />
        )}
        {editTarget && (
          <BulkEntryModal
            isOpen={true}
            type={editTarget.category}
            editItem={editTarget.item}
            onClose={() => setEditTarget(null)}
            onSave={handleEditSave}
          />
        )}
        {showSystemGuide && (
          <SystemGuideModal isOpen={showSystemGuide} onClose={() => setShowSystemGuide(false)} />
        )}
        {showDiagram && (
          <RoadmapDiagramModal isOpen={showDiagram} onClose={() => setShowDiagram(false)} />
        )}
        {showWalkthrough && (
          <RoadmapWalkthroughModal isOpen={showWalkthrough} onClose={() => setShowWalkthrough(false)} />
        )}
        {showWelcomeTour && (
          <WelcomeTourModal
            isOpen={showWelcomeTour}
            onClose={() => setShowWelcomeTour(false)}
            onStartRoadmap={() => setShowWalkthrough(true)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default Dashboard;